/**
 * Commodity Configuration Utility
 *
 * Centralized configuration for all commodities across towers.
 * Provides functional groups, packaging options, placeholders, and tower-specific settings.
 */

export const TOWER_CONFIG = {
  FB: {
    name: "F&B",
    industry: "Food & Beverage",
    brandColor: "#ff6034",
    apiPrefix: "/api/fb/generic",
  },
  BPCH: {
    name: "BPCH",
    industry: "Personal Care",
    brandColor: "#ff6034",
    apiPrefix: "/api/fb/generic", // Using FB generic route for BPCH as well
  },
};

export const COMMODITY_PLACEHOLDERS = {
  // F&B Tower
  Cookies: "e.g., Chocolate Chip Cookies, Oatmeal Cookies",
  "ICE CREAMS": "e.g., Vanilla Ice Cream, Chocolate Ice Cream",
  "Carbonated Drinks": "e.g., Cola, Lemon Soda",
  Meat: "e.g., Chicken Breast, Beef Patty",

  // BPCH Tower
  Shampoos: "e.g., Anti-Dandruff Shampoo, Moisturizing Shampoo",
  "Face Serum": "e.g., Vitamin C Serum, Hyaluronic Acid Serum",
  "Bar Soap": "e.g., Moisturizing Bar Soap, Antibacterial Soap",
  "Body Lotion": "e.g., Hydrating Body Lotion, Anti-Aging Lotion",
  Perfume: "e.g., Floral Perfume, Woody Perfume",
};

export const FUNCTIONAL_GROUPS = {
  // F&B Commodities
  Cookies: [
    "Bulk ingredient",
    "Sweetners",
    "Oil and fats",
    "Active ingredient",
    "Leavening agents",
    "Flavour enhancers",
    "Flavours",
    "Colours",
    "Preservatives",
    "Emulsifiers",
    "Stabilizers",
    "Thickeners",
    "Acidulants",
    "Vitamins and minerals",
    "Other",
  ],
  "ICE CREAMS": [
    "Dairy base",
    "Sweeteners",
    "Stabilizers",
    "Emulsifiers",
    "Flavours",
    "Colours",
    "Inclusions",
    "Fat source",
    "Protein source",
    "Other",
  ],
  "Carbonated Drinks": [
    "Water",
    "Sweeteners",
    "Acidulants",
    "Flavours",
    "Colours",
    "Preservatives",
    "Carbonation",
    "Vitamins and minerals",
    "Other",
  ],
  Meat: [
    "Protein source",
    "Seasoning",
    "Preservatives",
    "Binders",
    "Flavor enhancers",
    "Marinades",
    "Antioxidants",
    "Other",
  ],

  // BPCH Commodities
  Shampoos: [
    "Surfactants",
    "Conditioning agents",
    "Thickeners",
    "Preservatives",
    "Fragrances",
    "pH Adjusters",
    "Active ingredients",
    "Chelating agents",
    "Colorants",
    "Pearling agents",
    "Botanical extracts",
    "Proteins",
    "Vitamins",
    "Other",
  ],
  "Face Serum": [
    "Active ingredients",
    "Emollients",
    "Humectants",
    "Preservatives",
    "Fragrances",
    "pH Adjusters",
    "Antioxidants",
    "Peptides",
    "Vitamins",
    "Botanical extracts",
    "Thickeners",
    "Emulsifiers",
    "Other",
  ],
  "Bar Soap": [
    "Soap base",
    "Moisturizers",
    "Fragrances",
    "Colorants",
    "Exfoliants",
    "Antibacterial agents",
    "pH Adjusters",
    "Preservatives",
    "Botanical extracts",
    "Essential oils",
    "Other",
  ],
  "Body Lotion": [
    "Emollients",
    "Humectants",
    "Emulsifiers",
    "Preservatives",
    "Fragrances",
    "Active ingredients",
    "Thickeners",
    "pH Adjusters",
    "Vitamins",
    "Botanical extracts",
    "Antioxidants",
    "Colorants",
    "Other",
  ],
  Perfume: [
    "Top notes",
    "Middle notes",
    "Base notes",
    "Alcohol",
    "Fixatives",
    "Solvents",
    "Stabilizers",
    "Other",
  ],
};

export const PRIMARY_PACKAGING_OPTIONS = {
  Cookies: ["Flow Wrap", "Pouch", "Tray with Film", "Box", "Sleeve Wrapper", "Pillow Pouch"],
  "ICE CREAMS": ["Cup", "Cone", "Stick Wrapper", "Sandwich Wrapper", "Tub", "Family Tub"],
  "Carbonated Drinks": ["PET Bottle", "Glass Bottle", "Can", "Tetra Pack"],
  Meat: ["Tray with Overwrap", "Vacuum Pack", "Thermoformed Pack", "Pouch", "Can"],
  Shampoos: [
    "Plastic Bottle (HDPE)",
    "Plastic Bottle (PET)",
    "Sachet Pouch",
    "Glass Bottle",
    "Aluminum Bottle",
    "Tube",
    "Pump Bottle",
  ],
  "Face Serum": ["Glass Dropper Bottle", "Airless Pump Bottle", "Tube", "Sachet", "Ampoule"],
  "Bar Soap": ["Flow Wrap", "Carton Box", "Paper Wrap", "Sleeve", "Multipack"],
  "Body Lotion": ["Plastic Bottle", "Pump Bottle", "Tube", "Sachet", "Jar"],
  Perfume: ["Glass Bottle", "Roll-On Bottle", "Atomizer Bottle", "Mini Vial"],
};

const DEFAULT_PLAYBOOK_SLIDER_RANGES = {
  moisture: { min: 0, max: 100, step: 0.5 },
  process: { min: 0, max: 20, step: 0.5 },
  packaging: { min: 0, max: 20, step: 0.5 },
  ohp: { min: 0, max: 40, step: 0.5 },
};

const PLAYBOOK_SLIDER_RANGES = {
  FB: {
    default: {
      moisture: { min: 0, max: 100, step: 0.5 },
      process: { min: 0, max: 12, step: 0.5 },
      packaging: { min: 0, max: 8, step: 0.5 },
      ohp: { min: 5, max: 35, step: 0.5 },
    },
    Cookies: {
      moisture: { min: 0, max: 15, step: 0.5 },
      process: { min: 0, max: 10, step: 0.5 },
      packaging: { min: 0, max: 5, step: 0.5 },
      ohp: { min: 5, max: 35, step: 0.5 },
    },
    "ICE CREAMS": {
      moisture: { min: 45, max: 75, step: 0.5 },
      process: { min: 0, max: 10, step: 0.5 },
      packaging: { min: 0, max: 5, step: 0.5 },
      ohp: { min: 5, max: 35, step: 0.5 },
    },
    "Carbonated Drinks": {
      moisture: { min: 80, max: 100, step: 0.5 },
      process: { min: 0, max: 8, step: 0.5 },
      packaging: { min: 0, max: 5, step: 0.5 },
      ohp: { min: 5, max: 35, step: 0.5 },
    },
    Meat: {
      moisture: { min: 45, max: 80, step: 0.5 },
      process: { min: 0, max: 12, step: 0.5 },
      packaging: { min: 0, max: 6, step: 0.5 },
      ohp: { min: 5, max: 35, step: 0.5 },
    },
  },
  BPCH: {
    default: {
      moisture: { min: 45, max: 95, step: 0.5 },
      process: { min: 0, max: 10, step: 0.5 },
      packaging: { min: 0, max: 5, step: 0.5 },
      ohp: { min: 10, max: 40, step: 0.5 },
    },
    Shampoo: {
      moisture: { min: 60, max: 85, step: 0.5 },
      process: { min: 0, max: 8, step: 0.5 },
      packaging: { min: 0, max: 3, step: 0.5 },
      ohp: { min: 10, max: 40, step: 0.5 },
    },
    Shampoos: {
      moisture: { min: 60, max: 85, step: 0.5 },
      process: { min: 0, max: 8, step: 0.5 },
      packaging: { min: 0, max: 3, step: 0.5 },
      ohp: { min: 10, max: 40, step: 0.5 },
    },
    "Face Serum": {
      moisture: { min: 45, max: 85, step: 0.5 },
      process: { min: 0, max: 8, step: 0.5 },
      packaging: { min: 0, max: 4, step: 0.5 },
      ohp: { min: 10, max: 40, step: 0.5 },
    },
    "Body Lotion": {
      moisture: { min: 50, max: 85, step: 0.5 },
      process: { min: 0, max: 8, step: 0.5 },
      packaging: { min: 0, max: 4, step: 0.5 },
      ohp: { min: 10, max: 40, step: 0.5 },
    },
    Perfume: {
      moisture: { min: 70, max: 98, step: 0.5 },
      process: { min: 0, max: 6, step: 0.5 },
      packaging: { min: 0, max: 3, step: 0.5 },
      ohp: { min: 10, max: 45, step: 0.5 },
    },
    "Bar Soap": {
      moisture: { min: 5, max: 20, step: 0.5 },
      process: { min: 0, max: 8, step: 0.5 },
      packaging: { min: 0, max: 4, step: 0.5 },
      ohp: { min: 10, max: 40, step: 0.5 },
    },
  },
};

const findCommodityConfig = (configMap, commodity) => {
  const rawCommodity = String(commodity || "").trim();
  if (!rawCommodity) return null;

  if (Object.prototype.hasOwnProperty.call(configMap, rawCommodity)) {
    return configMap[rawCommodity];
  }

  const normalizedCommodity = rawCommodity.toLowerCase();
  const matchedKey = Object.keys(configMap).find(
    (key) => String(key || "").trim().toLowerCase() === normalizedCommodity,
  );

  return matchedKey ? configMap[matchedKey] : null;
};

const cloneRangeGroup = (ranges) =>
  Object.fromEntries(
    Object.entries(ranges || {}).map(([key, config]) => [key, { ...config }]),
  );

const fitRangeToValue = (range, rawValue, { minOverride = 0, upperPadding = 0 } = {}) => {
  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) {
    return {
      ...range,
      min: minOverride,
    };
  }

  const step = Number(range?.step) || 0.5;
  const roundToStep = (value, direction) => {
    const scaledValue = value / step;
    const rounded =
      direction === "down" ? Math.floor(scaledValue) * step : Math.ceil(scaledValue) * step;
    return Number(rounded.toFixed(4));
  };

  let min = minOverride;
  let max = Number(range?.max) || 0;

  if (numericValue < min) {
    min = roundToStep(numericValue, "down");
  }

  if (numericValue > max) {
    max = roundToStep(numericValue, "up");
  }

  if (upperPadding > 0 && numericValue >= max - step) {
    max = roundToStep(Math.max(max, numericValue + upperPadding), "up");
  }

  return {
    ...range,
    min,
    max,
  };
};

/**
 * Get functional groups for a specific commodity
 * @param {string} tower - Tower name (FB, BPCH)
 * @param {string} commodity - Commodity name
 * @returns {string[]} Array of functional group names
 */
export function getFunctionalGroups(tower, commodity) {
  return findCommodityConfig(FUNCTIONAL_GROUPS, commodity) || FUNCTIONAL_GROUPS.Cookies;
}

export function getPrimaryPackagingOptions(commodity) {
  const options = findCommodityConfig(PRIMARY_PACKAGING_OPTIONS, commodity);
  return Array.isArray(options) ? options : [];
}

export function getPlaybookSliderRanges(commodity, tower = "FB", data = {}) {
  const towerRanges = PLAYBOOK_SLIDER_RANGES[tower] || {};
  const commodityRanges =
    findCommodityConfig(towerRanges, commodity) ||
    towerRanges.default ||
    DEFAULT_PLAYBOOK_SLIDER_RANGES;
  const baseRanges = cloneRangeGroup(commodityRanges);

  return {
    moisture: fitRangeToValue(baseRanges.moisture, data?.moisture_content),
    process: fitRangeToValue(baseRanges.process, data?.process_loss),
    packaging: fitRangeToValue(baseRanges.packaging, data?.packaging_loss),
    ohp: fitRangeToValue(baseRanges.ohp, data?.ohp_percentage, { upperPadding: 10 }),
  };
}

/**
 * Get tower configuration
 * @param {string} tower - Tower name (FB, BPCH)
 * @returns {object} Tower configuration object
 */
export function getTowerConfig(tower) {
  return TOWER_CONFIG[tower] || TOWER_CONFIG.FB; // Default to FB
}

/**
 * Get product name placeholder for commodity
 * @param {string} commodity - Commodity name
 * @returns {string} Placeholder text
 */
export function getCommodityPlaceholder(commodity) {
  return findCommodityConfig(COMMODITY_PLACEHOLDERS, commodity) || "e.g., Product Name";
}

/**
 * Get all available commodities for a tower
 * @param {string} tower - Tower name
 * @returns {string[]} Array of commodity names
 */
export function getCommoditiesForTower(tower) {
  if (tower === "FB") {
    return ["Cookies", "ICE CREAMS", "Carbonated Drinks", "Meat"];
  } else if (tower === "BPCH") {
    return ["Shampoo", "Face Serum", "Bar Soap", "Body Lotion", "Perfume"];
  }
  return [];
}
